// src/components/auditoria/LogsPage.js
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import {
    FileText, Filter as FilterIcon, Download, Trash2, Loader2,
    XCircle, AlertTriangle, Info, AlertCircle, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import Pagination from '../common/Pagination';
import {
    useLogs, useLogsDashboard, useModulosAtivos, useMutateLogs,
} from '../../hooks/useLogs';
import { useAuth } from '../../context/auth/AuthProvider';
import useAlert from '../../hooks/useAlert';

const NIVEL_CONFIG = {
    info:    { label: 'Info',    badgeClass: 'badge-info',    Icon: Info,          bgClass: 'bg-blue-100 text-blue-800' },
    aviso:   { label: 'Aviso',   badgeClass: 'badge-warning', Icon: AlertTriangle,  bgClass: 'bg-yellow-100 text-yellow-800' },
    erro:    { label: 'Erro',    badgeClass: 'badge-danger',  Icon: AlertCircle,    bgClass: 'bg-orange-100 text-orange-800' },
    critico: { label: 'Crítico', badgeClass: 'badge-danger',  Icon: AlertCircle,    bgClass: 'bg-red-100 text-red-800' },
    debug:   { label: 'Debug',   badgeClass: '',              Icon: FileText,       bgClass: 'bg-gray-100 text-gray-600' },
};

const PERIODOS = [
    { value: '24h', label: 'Últimas 24h' },
    { value: '7d',  label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: '90d', label: 'Últimos 90 dias' },
];

const FILTROS_INICIAIS = {
    nivel: '', modulo: '', search: '', dataInicio: '', dataFim: '', sortBy: 'createdAt', sortOrder: '-1',
};

const NivelBadge = ({ nivel }) => {
    const cfg = NIVEL_CONFIG[nivel] || NIVEL_CONFIG.debug;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
        }} className={cfg.bgClass}>
            {cfg.label}
        </span>
    );
};

const LogDetalheModal = ({ log, onClose }) => {
    if (!log) return null;
    const contexto = log.contextoObj || log.contexto || {};
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 680, width: '95%',
                maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <NivelBadge nivel={log.nivel} />
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                            {log.dataFormatada || (log.createdAt ? new Date(log.createdAt).toLocaleString('pt-BR') : '—')}
                        </span>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                        <X size={20} />
                    </button>
                </div>

                <p style={{ fontWeight: 600, marginBottom: '1rem', lineHeight: 1.5 }}>{log.mensagem}</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    {[
                        ['Módulo', log.modulo],
                        ['Origem', log.origem],
                        ['Tipo', log.metadata?.tipo],
                        ['Ambiente', log.metadata?.ambiente],
                        ['IP', log.ip],
                        ['Versão', log.metadata?.versao],
                    ].map(([label, value]) => value ? (
                        <div key={label}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: '0.875rem' }}>{value}</div>
                        </div>
                    ) : null)}
                </div>

                {log.usuario?.name && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: 8 }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Usuário</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{log.usuario.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{log.usuario.email}</div>
                    </div>
                )}

                {log.userAgent && (
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 2 }}>User Agent</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', wordBreak: 'break-all' }}>{log.userAgent}</div>
                    </div>
                )}

                {Object.keys(contexto).length > 0 && (
                    <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 6 }}>Contexto</div>
                        <pre style={{
                            background: '#1e293b', color: '#e2e8f0', borderRadius: 8,
                            padding: '0.75rem', fontSize: '0.75rem', overflowX: 'auto',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                            {JSON.stringify(contexto, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

const LogsPage = () => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const [showFilters, setShowFilters] = useState(false);
    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
    const [activeFiltros, setActiveFiltros] = useState({ sortBy: 'createdAt', sortOrder: '-1' });
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const [periodoStats, setPeriodoStats] = useState('24h');
    const [showLimpar, setShowLimpar] = useState(false);
    const [limparConfig, setLimparConfig] = useState({ dias: 30, nivel: '' });
    const itemsPerPage = 50;

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: itemsPerPage,
        ...activeFiltros,
    }), [currentPage, activeFiltros, itemsPerPage]);

    const { data: logsData, isLoading, isFetching } = useLogs(queryParams);
    const { data: dashboard, isLoading: dashLoading } = useLogsDashboard(periodoStats);
    const { data: modulos = [] } = useModulosAtivos();
    const { limparAntigos, exportarCSV } = useMutateLogs();

    const logs = logsData?.data || [];
    const total = logsData?.total || logsData?.count || 0;

    // ✅ CORREÇÃO: GET /logs/dashboard devolve `porNivel` como array
    // ([{_id, total, percentual}]), nunca um `totalPorNivel` — esse campo não
    // existe na resposta. Os cards sempre caiam no fallback "—", mesmo
    // havendo erros/críticos reais no período selecionado.
    const totalPorNivel = useMemo(() => {
        const porNivel = dashboard?.porNivel || [];
        return porNivel.reduce((acc, item) => {
            acc[item._id] = item.total;
            return acc;
        }, {});
    }, [dashboard]);

    const statsCards = [
        { nivel: 'info',    valor: totalPorNivel.info    ?? '—', cor: '#3b82f6', label: 'Info' },
        { nivel: 'aviso',   valor: totalPorNivel.aviso   ?? '—', cor: '#f59e0b', label: 'Aviso' },
        { nivel: 'erro',    valor: totalPorNivel.erro    ?? '—', cor: '#f97316', label: 'Erro' },
        { nivel: 'critico', valor: totalPorNivel.critico ?? '—', cor: '#ef4444', label: 'Crítico' },
    ];

    const handleFiltroChange = useCallback((e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleAplicarFiltros = useCallback((e) => {
        if (e) e.preventDefault();
        setActiveFiltros(filtros);
        setCurrentPage(1);
    }, [filtros]);

    const handleLimparFiltros = useCallback(() => {
        setFiltros(FILTROS_INICIAIS);
        setActiveFiltros({ sortBy: 'createdAt', sortOrder: '-1' });
        setCurrentPage(1);
    }, []);

    const handleExportar = useCallback(async () => {
        try {
            await exportarCSV({
                dataInicio: activeFiltros.dataInicio,
                dataFim: activeFiltros.dataFim,
                nivel: activeFiltros.nivel,
                modulo: activeFiltros.modulo,
            });
        } catch {
            setAlert('Erro ao exportar logs.', 'danger');
        }
    }, [exportarCSV, activeFiltros, setAlert]);

    const handleLimpar = useCallback(() => {
        if (window.confirm(`Tem certeza que deseja excluir logs com mais de ${limparConfig.dias} dias? Esta ação não pode ser desfeita.`)) {
            limparAntigos.mutate(limparConfig, {
                onSuccess: () => setShowLimpar(false),
            });
        }
    }, [limparConfig, limparAntigos]);

    const hasFilters = Object.entries(filtros).some(([k, v]) => !['sortBy', 'sortOrder'].includes(k) && v !== '');

    return (
        <Fragment>
            {selectedLog && <LogDetalheModal log={selectedLog} onClose={() => setSelectedLog(null)} />}

            <div className="page-management-container">
                <PageHeader
                    icon={<FileText />}
                    title="Logs do Sistema"
                    subtitle={!isLoading && total > 0 ? `${total.toLocaleString('pt-BR')} registro${total !== 1 ? 's' : ''}` : null}
                    actions={<>
                        <button
                            onClick={() => setShowFilters(p => !p)}
                            className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                        >
                            <FilterIcon size={18} className="icon"/>
                            Filtros
                        </button>
                        <button onClick={handleExportar} className="action-button action-button-secondary">
                            <Download size={18} className="icon"/>
                            Exportar CSV
                        </button>
                        <button
                            onClick={() => setShowLimpar(p => !p)}
                            className="action-button action-button-danger"
                        >
                            <Trash2 size={18} className="icon"/>
                            Limpar Antigos
                        </button>
                    </>}
                />

                {/* Cards de métricas */}
                {!dashLoading && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        {statsCards.map(card => (
                            <div
                                key={card.nivel}
                                className="kpi-card"
                                style={{ cursor: 'pointer', borderLeft: `4px solid ${card.cor}` }}
                                onClick={() => {
                                    setFiltros(prev => ({ ...prev, nivel: card.nivel }));
                                    setActiveFiltros(prev => ({ ...prev, nivel: card.nivel }));
                                    setCurrentPage(1);
                                }}
                            >
                                <h5 className="kpi-card__title">{card.label}</h5>
                                <p className="kpi-card__value" style={{ color: card.cor }}>{card.valor}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Seletor de período dos stats */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Período dos métricas:</span>
                    {PERIODOS.map(p => (
                        <button
                            key={p.value}
                            onClick={() => setPeriodoStats(p.value)}
                            className={`action-button ${periodoStats === p.value ? 'action-button-primary' : 'action-button-secondary'}`}
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Painel de Filtros */}
                {showFilters && (
                    <div className="content-panel">
                        <form onSubmit={handleAplicarFiltros}>
                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Nível</label>
                                    <select name="nivel" value={filtros.nivel} onChange={handleFiltroChange} className="form-input">
                                        <option value="">Todos</option>
                                        {Object.entries(NIVEL_CONFIG).map(([k, v]) => (
                                            <option key={k} value={k}>{v.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Módulo</label>
                                    <select name="modulo" value={filtros.modulo} onChange={handleFiltroChange} className="form-input">
                                        <option value="">Todos</option>
                                        {/* ✅ CORREÇÃO: GET /logs/modulos/ativos devolve objetos de estatística
                                            ({_id, modulo, total, ultimo, erros} — ver logsController.js), não uma
                                            lista de nomes. Renderizar `m` direto tentava colocar o objeto inteiro
                                            como filho do React, quebrando a página inteira ao abrir o painel de
                                            filtros. */}
                                        {modulos.map(m => {
                                            const nomeModulo = m?.modulo || m?._id;
                                            if (!nomeModulo) return null;
                                            return <option key={nomeModulo} value={nomeModulo}>{nomeModulo}</option>;
                                        })}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Data Início</label>
                                    <input type="datetime-local" name="dataInicio" value={filtros.dataInicio} onChange={handleFiltroChange} className="form-input"/>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Data Fim</label>
                                    <input type="datetime-local" name="dataFim" value={filtros.dataFim} onChange={handleFiltroChange} className="form-input"/>
                                </div>
                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label className="form-label">Busca Textual</label>
                                    <input
                                        name="search"
                                        value={filtros.search}
                                        onChange={handleFiltroChange}
                                        className="form-input"
                                        placeholder="Buscar na mensagem, origem..."
                                    />
                                </div>
                            </div>
                            <div className="actions-bar" style={{ justifyContent: 'flex-end' }}>
                                {hasFilters && (
                                    <button type="button" onClick={handleLimparFiltros} className="action-button action-button-secondary">
                                        <XCircle size={18} className="icon"/> Limpar Filtros
                                    </button>
                                )}
                                <button type="submit" className="action-button action-button-primary">
                                    <FilterIcon size={18} className="icon"/> Aplicar
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Painel Limpar Antigos */}
                {showLimpar && (
                    <div className="content-panel" style={{ borderLeft: '4px solid var(--color-danger)' }}>
                        <div className="form-section">
                            <h4 className="form-section-title" style={{ color: 'var(--color-danger)' }}>
                                Limpar Logs Antigos
                            </h4>
                        </div>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Remover logs com mais de (dias)</label>
                                <input
                                    type="number"
                                    min="7"
                                    value={limparConfig.dias}
                                    onChange={e => setLimparConfig(prev => ({ ...prev, dias: Number(e.target.value) }))}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Nível (opcional)</label>
                                <select
                                    value={limparConfig.nivel}
                                    onChange={e => setLimparConfig(prev => ({ ...prev, nivel: e.target.value }))}
                                    className="form-input"
                                >
                                    <option value="">Todos</option>
                                    {Object.entries(NIVEL_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="actions-bar" style={{ justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowLimpar(false)} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Cancelar
                            </button>
                            <button
                                onClick={handleLimpar}
                                className="action-button action-button-danger"
                                disabled={limparAntigos.isPending}
                            >
                                {limparAntigos.isPending ? <Loader2 size={18} className="icon animate-spin"/> : <Trash2 size={18} className="icon"/>}
                                Confirmar Limpeza
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin"/>
                            <span>Carregando logs...</span>
                        </div>
                    ) : logs.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 90 }}>Nível</th>
                                    <th style={{ textAlign: 'left' }}>Mensagem</th>
                                    <th>Módulo</th>
                                    <th>Tipo</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr
                                        key={log._id}
                                        onClick={() => setSelectedLog(log)}
                                        style={{ cursor: 'pointer' }}
                                        className={`${log.nivel === 'critico' ? 'bg-red-50' : log.nivel === 'erro' ? 'bg-orange-50' : ''}`}
                                    >
                                        <td><NivelBadge nivel={log.nivel}/></td>
                                        <td style={{ textAlign: 'left' }}>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 500, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {log.mensagem}
                                            </div>
                                            {log.origem && (
                                                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>{log.origem}</div>
                                            )}
                                        </td>
                                        <td>
                                            {log.modulo && <span className="badge badge-info">{log.modulo}</span>}
                                        </td>
                                        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                            {log.metadata?.tipo || '—'}
                                        </td>
                                        <td style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                            {log.dataFormatada || (log.createdAt ? new Date(log.createdAt).toLocaleString('pt-BR') : '—')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <FileText size={48}/>
                            <h3>Nenhum log encontrado</h3>
                            <p>{hasFilters ? 'Tente ajustar os filtros.' : 'Não há logs registrados no momento.'}</p>
                        </div>
                    )}
                </div>
                <Pagination
                    totalItems={total}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Fragment>
    );
};

export default LogsPage;
